import type { ClientBuild } from "@/exports/build/client";
import type {
  PluginClientAccessor,
  PluginClientAtoms,
  PluginClientConfig,
  PluginClientDefinition,
} from "@/plugins/client/types";
import type { PluginConfig } from "@/plugins/server/types";
import type { ToMethodName } from "@/shared/method-name";
import type * as op from "@/shared/operation";
import type { Any, Opaque, Override, Without } from "@/shared/types";
import type { AgentDefinition } from "../server/types";
import type { AgentClientBuilder } from "./builder";
import type { AgentClient } from "./class";

// Plugins
export type AgentClientPluginsDefinition = PluginClientDefinition[];

// Definition
export type AgentClientDefinition = {
  name: string;
  plugins: AgentClientPluginsDefinition;
  pluginConfigs: Record<string, unknown>;
  $serverDef: AgentDefinition;
};

// Builder
// - Helper to return the builder without the excluded methods and with the plugins methods
export type TypedAgentClientBuilder<
  AgentClientDef extends AgentClientDefinition,
  Excluded extends string = never,
> = Omit<
  AgentClientBuilder<AgentClientDef, Excluded> & {
    // Add plugin methods to the builder
    [PluginDef in AgentClientDef["plugins"][number] as ToMethodName<
      PluginDef["name"]
    >]: AgentClientBuilderPluginMethod<PluginDef["name"], AgentClientDef, Excluded>;
  },
  Excluded
>;

type AgentClientBuilderPluginMethod<
  PluginName extends string,
  AgentClientDef extends AgentClientDefinition,
  Excluded extends string = never,
> = <
  const C extends PluginClientConfig<
    Extract<AgentClientDef["plugins"][number], { name: PluginName }>["config"],
    "input"
  >,
>(
  config: C,
) => PluginName extends string
  ? TypedAgentClientBuilder<
      AgentClientDef & {
        pluginConfigs: {
          [Key in PluginName]: PluginClientConfig<
            Extract<AgentClientDef["plugins"][number], { name: PluginName }>["config"],
            "output"
          > &
            C;
        };
      },
      Excluded | PluginName
    >
  : never;

// Client
// - Simulates the shape of an agent client having specific plugin(s) registered
export declare const __plugins: unique symbol;
export type AgentClientParam<
  Plugins extends { def: PluginClientDefinition }[] = [],
  _internal extends boolean = true,
> = Opaque<
  op.ToPublic<
    AgentClient<AgentClientDefinition> & {
      [Plugin in Plugins[number] as Plugin["def"]["name"]]: Override<
        PluginClientAccessor<
          Plugin["def"],
          {
            client: PluginClientConfig<Plugin["def"]["config"], "output">;
            server: PluginConfig<Plugin["def"]["$serverDef"]["config"], "output">;
          }
        >,
        _internal extends true ? "atoms" : never,
        Any
      >;
    }
  >
> & { [__plugins]?: Plugins };

export const parseAgentClientParam = <
  Agent extends op.ToPublic<AgentClient<AgentClientDefinition>> | null,
>(
  agent: Agent,
) =>
  agent as Agent extends { [__plugins]?: infer Plugins extends { def: PluginClientDefinition }[] }
    ? AgentClientParam<Plugins, false>
    : null;

// - Typed client from generated build
export type AgentClientFromBuild<Name extends keyof ClientBuild> = op.ToPublic<
  AgentClient<ClientBuild[Name]["definition"]> & {
    [PluginName in keyof ClientBuild[Name]["plugins"]]: Without<
      Override<
        PluginClientAccessor<
          ClientBuild[Name]["plugins"][PluginName]["def"],
          // @ts-expect-error
          {
            client: ClientBuild[Name]["plugins"][PluginName]["$types"]["clientConfig"];
            server: ClientBuild[Name]["plugins"][PluginName]["$types"]["serverConfig"];
          }
        >,
        "atoms",
        PluginClientAtoms<() => ClientBuild[Name]["plugins"][PluginName]["$types"]["atoms"]>
      >,
      keyof InstanceType<ClientBuild[Name]["plugins"][PluginName]["$types"]["class"]>
    > &
      InstanceType<ClientBuild[Name]["plugins"][PluginName]["$types"]["class"]>;
  }
>;
